<template>
  <v-card class="pb-5" elevation="0">
    <v-card-text class="ma-0 pa-5">
      <v-form ref="form" v-model="valid">
        <v-row>
          <v-divider />
        </v-row>

        <v-row>
          <v-col>
            <v-radio-group v-model="choice">
              <v-radio
                :label="$t('booking.bookRoom')"
                value="0"
                color="#656565"
                :disabled="!allDesksAreBookable"
              />
              <v-radio
                :label="$t('booking.chooseDesksToBook')"
                value="1"
                color="#656565"
                :disabled="!anyAvailableDesks || noDesksAreBookable"
              />
            </v-radio-group>

            <v-container class="pa-1 item-container" v-if="anyAvailableDesks">
              <v-item-group
                v-model="selectedIndexes"
                multiple
                class="item-group"
              >
                <div
                  v-for="(item, i) in availableDesks"
                  :key="i"
                  style="display: inline-block"
                >
                  <v-item v-slot="{ active, toggle }" class="ma-1">
                    <v-tooltip top :disabled="isItemBookable(item)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-sheet
                          v-bind="attrs"
                          v-on="on"
                          class="mr-2 mb-2"
                          :color="
                            isItemBookable(item) && !deskSelectionDisabled
                              ? 'white'
                              : '#f0f0f080'
                          "
                          elevation="1"
                          height="80"
                          width="150"
                          @click="onItemClick(item, toggle)"
                        >
                          <v-container>
                            <v-row>
                              <div
                                style="display: flex; flex-direction: column"
                                class="pt-1 pl-2 text-subtitle-1"
                              >
                                <span class="text-subtitle-1">
                                  {{ item.name }}
                                </span>
                                <span
                                  v-if="!isItemBookable(item)"
                                  class="red--text"
                                >
                                  {{ $t("booking.itemUnavailable") }}
                                </span>
                              </div>
                              <v-spacer />
                              <v-btn
                                icon
                                :disabled="
                                  !isItemBookable(item) || deskSelectionDisabled
                                "
                              >
                                <v-icon>
                                  {{
                                    active
                                      ? "mdi-checkbox-marked-circle"
                                      : "mdi-checkbox-blank-circle-outline"
                                  }}
                                </v-icon>
                              </v-btn>
                            </v-row>
                          </v-container>
                        </v-sheet>
                      </template>
                      <span v-html="getBookedInfo(item)"></span>
                    </v-tooltip>
                  </v-item>
                </div>
              </v-item-group>
            </v-container>
          </v-col>
        </v-row>

        <v-row v-if="noDesksAreBookable">
          <v-col class="text-subtitle-1 mt-0 pt-0 mb-0 pb-0 red--text">
            {{ $t("booking.noDesksAreBookable") }}
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions class="ma-0 pa-0">
      <v-spacer />

      <v-btn color="red darken-1" text :disabled="!canGoBack" @click="goBack">
        {{ $t("booking.stepperBack") }}
      </v-btn>
      <v-btn color="red darken-1" text :disabled="!canGoNext" @click="goNext">
        {{ $t("booking.stepperNext") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { DateTime } from "luxon";

export default {
  name: "ChooseDesksStep",
  components: {},
  props: {
    booking: null,
    desksAvailability: null,
    canGoBack: false,
  },
  data: () => ({
    valid: false,
    choice: "0",
    selectedIndexes: [],
  }),
  computed: {
    ...mapGetters("deskModule", {
      desks: "getDesks",
      availableDesksByRoom: "getAvailableDesksByRoom",
    }),
    ...mapGetters("bookingModule", {
      roomDesksBookedOnInterval: "getRoomDesksBookedOnInterval",
    }),

    availableDesks() {
      return this.availableDesksByRoom(this.booking.room);
    },
    anyAvailableDesks() {
      return this.availableDesks.length > 0;
    },
    bookedDesks() {
      if (!this.booking.interval) return;
      return this.roomDesksBookedOnInterval(
        this.booking.room,
        this.availableDesks,
        this.booking.interval
      );
    },
    deskSelectionDisabled() {
      return this.choice !== "1";
    },
    canGoNext() {
      return (
        (this.someDesksAreBookable &&
          (this.deskSelectionDisabled ||
            (!this.deskSelectionDisabled &&
              this.selectedIndexes.length > 0))) ||
        (!this.someDesksAreBookable &&
          this.deskSelectionDisabled &&
          !this.anyAvailableDesks)
      );
    },

    noDesksAreBookable() {
      return this.availableDesks.every((desk) => !this.isItemBookable(desk));
    },
    someDesksAreBookable() {
      return this.availableDesks.some((desk) => this.isItemBookable(desk));
    },
    allDesksAreBookable() {
      return this.availableDesks.every((desk) => this.isItemBookable(desk));
    },
  },
  methods: {
    // navigation
    reset() {
      this.choice = "0";
      this.selectedIndexes = [];
    },
    show() {
      // change choice depending of desks availability
      if (this.choice === "0" && !this.allDesksAreBookable)
        this.choice = this.noDesksAreBookable ? "2" : "1";
      else if (this.choice === "1" && this.noDesksAreBookable)
        this.choice = "2";
      else if (this.choice === "2")
        this.choice = this.allDesksAreBookable ? "0" : "1";

      // only keep selection of bookable desks
      const selectedIndexes = [];
      for (const i of this.selectedIndexes) {
        if (this.isItemBookable(this.availableDesks[i]))
          selectedIndexes.push(i);
      }
      this.selectedIndexes = selectedIndexes;

      this.updateAvailability();
    },
    hide() {},
    goBack() {
      this.$emit("goBack");
    },
    goNext() {
      this.booking.desks = this.getSelectedDesks();
      this.booking.wholeRoom = this.deskSelectionDisabled;
      this.$emit("goNext");
    },
    updateAvailability() {
      // indicate that some desks are bookable
      this.desksAvailability.value = this.someDesksAreBookable;
    },

    onItemClick(item, toggle) {
      if (!this.deskSelectionDisabled && this.isItemBookable(item)) toggle();
    },
    isItemBookable(item) {
      return !this.bookedDesks ? false : !this.bookedDesks.desks.has(item);
    },
    getSelectedDesks() {
      if (this.deskSelectionDisabled) return [];
      const result = [];
      for (const i of this.selectedIndexes) result.push(this.availableDesks[i]);
      return result;
    },
    getBookedInfo(item) {
      const bookings = this.bookedDesks?.bookings.get(item);
      if (!bookings) return "";
      let sb = '<span class="red">Réservé :</span><br/>';
      bookings.forEach(
        (booking) =>
          (sb += `${booking.interval.start.toLocaleString(
            DateTime.DATETIME_SHORT
          )} à ${booking.interval.end.toLocaleString(
            DateTime.DATETIME_SHORT
          )}<br/>`)
      );
      return sb;
    },
  },
  watch: {
    someDesksAreBookable(newValue) {
      this.desksAvailability.value = newValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.item-container {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 200px;
}

.item-group {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 7px;
}
</style>
